import { X } from "lucide-react";
import React, { useMemo, useState } from "react";

import type { FilterOption, LineKey } from "@/api/order_journal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtQty } from "@/lib/order-journal";

/**
 * Фильтр по товару заказа: номенклатура → характеристика → партия, из строк,
 * которые реально отсканированы в этом заказе (`filters` журнала). Пустая
 * характеристика/партия — «любая» (так их понимает сервер). Поиск сужает
 * список номенклатуры по названию и артикулу.
 */
export function ProductFilter({
  options,
  value,
  onChange,
}: {
  options: FilterOption[];
  value: LineKey | null;
  onChange: (next: LineKey | null) => void;
}): React.ReactElement {
  const [search, setSearch] = useState("");

  const noms = useMemo(() => {
    const m = new Map<string, { ref: string; title: string; qty: number }>();
    for (const o of options) {
      const cur = m.get(o.nomenclature_ref);
      if (cur) cur.qty += o.qty_base;
      else
        m.set(o.nomenclature_ref, {
          ref: o.nomenclature_ref,
          title: [o.nomenclature_article, o.nomenclature_name].filter(Boolean).join(" "),
          qty: o.qty_base,
        });
    }
    return [...m.values()];
  }, [options]);

  const needle = search.trim().toLowerCase();
  const shownNoms = noms.filter(
    (n) => !needle || n.title.toLowerCase().includes(needle) || n.ref === value?.nomenclature_ref,
  );

  const nomRef = value?.nomenclature_ref ?? "";
  const ofNom = options.filter((o) => o.nomenclature_ref === nomRef);
  const chars = uniq(
    ofNom.filter((o) => o.characteristic_ref),
    (o) => o.characteristic_ref,
    (o) => o.characteristic_name,
  );
  const charRef = value?.characteristic_ref ?? "";
  const partias = uniq(
    ofNom.filter((o) => o.partia_ref && (!charRef || o.characteristic_ref === charRef)),
    (o) => o.partia_ref,
    (o) => o.partia_name,
  );

  const set = (nom: string, char: string, partia: string) =>
    onChange(nom ? { nomenclature_ref: nom, characteristic_ref: char, partia_ref: partia } : null);

  return (
    <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-end">
      <label className="block min-w-0 lg:w-56">
        <span className="mb-1 block text-xs text-muted-foreground">Поиск товара</span>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Название или артикул"
          aria-label="Поиск товара"
        />
      </label>
      <label className="block min-w-0 lg:flex-1">
        <span className="mb-1 block text-xs text-muted-foreground">Номенклатура</span>
        <select
          aria-label="Номенклатура"
          value={nomRef}
          onChange={(e) => set(e.target.value, "", "")}
          className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Все товары ({noms.length})</option>
          {shownNoms.map((n) => (
            <option key={n.ref} value={n.ref}>
              {n.title} — {fmtQty(n.qty)}
            </option>
          ))}
        </select>
      </label>
      {nomRef && chars.length > 0 && (
        <label className="block min-w-0 lg:w-48">
          <span className="mb-1 block text-xs text-muted-foreground">Характеристика</span>
          <select
            aria-label="Характеристика"
            value={charRef}
            onChange={(e) => set(nomRef, e.target.value, "")}
            className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">Любая</option>
            {chars.map((c) => (
              <option key={c.ref} value={c.ref}>
                {c.name || "без названия"}
              </option>
            ))}
          </select>
        </label>
      )}
      {nomRef && partias.length > 0 && (
        <label className="block min-w-0 lg:w-48">
          <span className="mb-1 block text-xs text-muted-foreground">Партия</span>
          <select
            aria-label="Партия"
            value={value?.partia_ref ?? ""}
            onChange={(e) => set(nomRef, charRef, e.target.value)}
            className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">Любая</option>
            {partias.map((p) => (
              <option key={p.ref} value={p.ref}>
                {p.name || "без названия"}
              </option>
            ))}
          </select>
        </label>
      )}
      {value && (
        <Button
          variant="outline"
          onClick={() => {
            setSearch("");
            onChange(null);
          }}
        >
          <X className="size-4" /> Сбросить
        </Button>
      )}
    </div>
  );
}

function uniq<T>(
  rows: T[],
  ref: (r: T) => string,
  name: (r: T) => string,
): { ref: string; name: string }[] {
  const m = new Map<string, string>();
  for (const r of rows) if (!m.has(ref(r))) m.set(ref(r), name(r));
  return [...m.entries()]
    .map(([r, n]) => ({ ref: r, name: n }))
    .sort((a, b) => a.name.localeCompare(b.name, "ru"));
}
